import type { BoxState, OrderChannel, SkuDto } from '@pl/domain';

const CHANNEL: Record<OrderChannel, string> = { shopify: 'Shopify', amazon: 'Amazon', sales: 'Phone', whatsapp: 'WhatsApp', invoice: 'Invoice', other: 'Other' };
export const channelName = (c: OrderChannel) => CHANNEL[c];
export const CHANNEL_DOT: Record<OrderChannel, string> = { shopify: '#5e8e3e', amazon: '#f29d1a', sales: '#6b6f76', whatsapp: '#25a35a', invoice: 'var(--graphite)', other: 'var(--faint)' };

const STATE: Record<BoxState, string> = {
  labelled: 'Not received',
  in_stock: 'In stock',
  picked: 'At dock',
  shipped: 'Shipped',
  in_transit: 'In transit',
  written_off: 'Written off',
  voided: 'Voided',
};
export const stateWords = (s: BoxState) => STATE[s];

export const skuName = (sku?: Pick<SkuDto, 'name' | 'colourName'> | null) => (sku ? (sku.colourName ? `${sku.name} ${sku.colourName}` : sku.name) : 'Blank sticker');

export function shortDateTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone });
}

/**
 * `shortDateTime` in the zone the time belongs to, with that zone's name: "Sep 23, 3:44 PM ET".
 * A box received in Maryland then reads the same on a laptop in California or Karachi. US zones
 * get their generic name (ET, PT, CT), the same on both sides of a DST change; any other zone gets
 * the short name Intl has for it ("UTC", "GMT+5"). No zone means the viewer's own, still named.
 */
export function zonedDateTime(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  try {
    return `${d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone })} ${zoneName(d, timeZone)}`.trimEnd();
  } catch {
    return shortDateTime(iso); // a zone this browser does not know
  }
}

/**
 * A zone's name at an instant, the one naming every labelled time uses: the generic name for US
 * zones (ET, PT, CT), the same on both sides of a DST change; any other zone's short name ("UTC",
 * "GMT+5"). No zone means the viewer's own, still named.
 */
export function zoneName(d: Date, timeZone: string | undefined): string {
  const name = (timeZoneName: 'shortGeneric' | 'short') => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName }).formatToParts(d).find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      return ''; // `shortGeneric` is newer than some engines
    }
  };
  const generic = name('shortGeneric');
  return /^[A-Z]{1,4}$/.test(generic) ? generic : name('short');
}

export function dayLabel(date: Date, timeZone: string): string {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone });
}

/** `"2.25x1.25"` → `"2.25 × 1.25 in"`. */
export function stickerInches(size: string): string {
  const [across, down] = size.split('x');
  return across && down ? `${across} × ${down} in` : `${size} in`;
}

/** `"2.25x1.25"` → `"57 × 32 mm"`, to the nearest millimetre, the way label rolls are sold outside the US. */
export function stickerMillimetres(size: string): string {
  const [across, down] = size.split('x').map(Number);
  return across && down ? `${Math.round(across * 25.4)} × ${Math.round(down * 25.4)} mm` : '';
}

/** "14:32:08" at the site, for a feed read second by second. The viewer's own clock if the zone is unknown. */
export function clockTime(iso: string, timeZone?: string): string {
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' };
  try {
    return new Date(iso).toLocaleTimeString('en-US', { ...opts, timeZone });
  } catch {
    return new Date(iso).toLocaleTimeString('en-US', opts); // a zone this browser does not know
  }
}
